import { useLayoutEffect, useState } from "react";

export function measureComposerMultilinePrompt(body: HTMLElement): boolean | null {
  const editor = body.querySelector<HTMLElement>('[data-testid="composer-editor"]');
  if (!editor || editor.clientWidth === 0) return null;

  const bodyStyle = getComputedStyle(body);
  const expandedWidth =
    body.clientWidth -
    Number.parseFloat(bodyStyle.paddingLeft) -
    Number.parseFloat(bodyStyle.paddingRight);
  const lineHeight = Number.parseFloat(getComputedStyle(editor).lineHeight);
  const range = document.createRange();
  range.selectNodeContents(editor);
  const bounds = range.getBoundingClientRect();

  // Measure content, not the editor's minimum height. While resting, the
  // prompt is unwrapped: compare its width with the expanded row so that
  // moving the actions inline cannot make collapse and expansion oscillate.
  return bounds.height > lineHeight + 1 || bounds.width > expandedWidth + 1;
}

export function useComposerMultilinePrompt(
  body: HTMLElement | null,
  prompt: string,
): boolean | null {
  const [measurement, setMeasurement] = useState<{
    body: HTMLElement;
    prompt: string;
    multiline: boolean;
  } | null>(null);

  useLayoutEffect(() => {
    if (!body) return;
    const measure = () => {
      const next = measureComposerMultilinePrompt(body);
      if (next !== null) {
        setMeasurement((previous) =>
          previous?.body === body && previous.prompt === prompt && previous.multiline === next
            ? previous
            : { body, prompt, multiline: next },
        );
      }
    };
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(body);
    const editor = body.querySelector<HTMLElement>('[data-testid="composer-editor"]');
    if (editor) resizeObserver.observe(editor);
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(body, { childList: true, characterData: true, subtree: true });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [body, prompt]);

  // A new or restored draft must remain measurable before mobile collapse hides it.
  return measurement?.body === body && measurement.prompt === prompt ? measurement.multiline : null;
}
